import { EventType } from "@/analytics/analytics-events";
import {
  AddAnnotationAttachmentsEventProperties,
  DeleteAnnotationAttachmentEventProperties,
} from "@/analytics/annotation-events-properties";
import { FileDetails, getFileExtension } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { useCallback } from "react";
import {
  AnnotationFormVariant,
  FileAttachment,
  useAnnotationAttachmentsFUI,
} from "./use-annotation-attachments-fui";

/**
 * @returns the list of attachments and a function to add a new ones to the list, by taking care of all Analytics events
 * @param fileAttachments IElements list of the annotation's attachments
 * @param formVariant type of the annotation's form
 * @param initialAttachment Optional client-side generated attachment (e.g. a screenshot) to pre-seed the list with on mount
 */
export function useAnnotationAttachments(
  fileAttachments: FileAttachment[],
  formVariant: AnnotationFormVariant,
  initialAttachment?: FileDetails,
): {
  /** the current list of file attachments */
  attachments: FileDetails[];
  /** function to append a new attachment */
  addNewAttachment(attachment: FileDetails, viaDrag: boolean): void;
} {
  const onDeleteAttachment = useCallback(
    (fileType: string, isNewAttachment: boolean) =>
      Analytics.track<DeleteAnnotationAttachmentEventProperties>(
        EventType.deleteAnnotationAttachment,
        {
          fileType,
          isNewAttachment,
        },
      ),
    [],
  );

  const onConfirmAttachmentDeletion = useCallback(
    () => Analytics.track(EventType.confirmAnnotationDeletion),
    [],
  );

  const onCancelAttachmentDeletion = useCallback(
    () => Analytics.track(EventType.cancelAnnotationDeletion),
    [],
  );

  const { attachments, addNewAttachment } = useAnnotationAttachmentsFUI(
    fileAttachments,
    formVariant,
    onDeleteAttachment,
    onConfirmAttachmentDeletion,
    onCancelAttachmentDeletion,
    initialAttachment,
  );

  const onNewAttachmentAdded = useCallback(
    (attachment: FileDetails, viaDrag: boolean) => {
      Analytics.track<AddAnnotationAttachmentsEventProperties>(
        EventType.addAnnotationAttachments,
        {
          viaDrag,
          fileType: getFileExtension(attachment.fileName) ?? "unknown",
        },
      );
      addNewAttachment(attachment);
    },
    [addNewAttachment],
  );

  return { attachments, addNewAttachment: onNewAttachmentAdded };
}
